/**
 * Intercom API Service
 * 
 * This service handles all interactions with the Intercom API for the Private Health Consultant Hub.
 * It provides methods for fetching conversations, sending messages, and updating conversation status.
 */

// Intercom API configuration
const INTERCOM_API_BASE_URL = 'https://api.intercom.io';
const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID;
const INTERCOM_ACCESS_TOKEN = process.env.REACT_APP_INTERCOM_ACCESS_TOKEN;

/**
 * Fetch conversations from Intercom
 * @param {Object} filters - Filters to apply to the conversation list
 * @returns {Promise<Array>} - List of conversations
 */
export const fetchIntercomConversations = async (filters = {}) => {
  try {
    const response = await fetch(`${INTERCOM_API_BASE_URL}/conversations`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${INTERCOM_ACCESS_TOKEN}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Error fetching conversations: ${response.statusText}`);
    }

    const data = await response.json();
    return data.conversations || [];
  } catch (error) {
    console.error('Error fetching Intercom conversations:', error);
    throw error;
  }
};

/**
 * Fetch a single conversation by ID
 * @param {string} conversationId - The Intercom conversation ID
 * @returns {Promise<Object>} - Conversation details
 */
export const fetchIntercomConversation = async (conversationId) => {
  try {
    const response = await fetch(`${INTERCOM_API_BASE_URL}/conversations/${conversationId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${INTERCOM_ACCESS_TOKEN}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Error fetching conversation: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error fetching Intercom conversation ${conversationId}:`, error);
    throw error;
  }
};

/**
 * Send a reply to an Intercom conversation
 * @param {string} conversationId - The Intercom conversation ID
 * @param {string} message - The message to send
 * @param {string} adminId - The ID of the admin sending the message
 * @returns {Promise<Object>} - Response from the API
 */
export const replyToIntercomConversation = async (conversationId, message, adminId) => {
  try {
    const response = await fetch(`${INTERCOM_API_BASE_URL}/conversations/${conversationId}/reply`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${INTERCOM_ACCESS_TOKEN}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: 'admin',
        admin_id: adminId,
        message_type: 'comment',
        body: message
      })
    });

    if (!response.ok) {
      throw new Error(`Error replying to conversation: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error replying to Intercom conversation ${conversationId}:`, error);
    throw error;
  }
};

/**
 * Assign a conversation to a specific admin
 * @param {string} conversationId - The Intercom conversation ID
 * @param {string} adminId - The ID of the admin to assign the conversation to
 * @returns {Promise<Object>} - Response from the API
 */
export const assignIntercomConversation = async (conversationId, adminId) => {
  try {
    const response = await fetch(`${INTERCOM_API_BASE_URL}/conversations/${conversationId}/parts`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${INTERCOM_ACCESS_TOKEN}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: 'assignment',
        assigned_to: adminId
      })
    });

    if (!response.ok) {
      throw new Error(`Error assigning conversation: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error assigning Intercom conversation ${conversationId}:`, error);
    throw error;
  }
};

/**
 * Close an Intercom conversation
 * @param {string} conversationId - The Intercom conversation ID
 * @param {string} adminId - The ID of the admin closing the conversation
 * @returns {Promise<Object>} - Response from the API
 */
export const closeIntercomConversation = async (conversationId, adminId) => {
  try {
    const response = await fetch(`${INTERCOM_API_BASE_URL}/conversations/${conversationId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${INTERCOM_ACCESS_TOKEN}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: 'admin',
        admin_id: adminId,
        message_type: 'close'
      })
    });

    if (!response.ok) {
      throw new Error(`Error closing conversation: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error closing Intercom conversation ${conversationId}:`, error);
    throw error;
  }
};

/**
 * Get the health data collected by the AI from conversation tags or custom attributes
 * @param {Object} conversation - The Intercom conversation object
 * @returns {Object} - Structured health data
 */
export const extractHealthDataFromConversation = (conversation) => {
  // In a real implementation, this would extract data from conversation tags,
  // custom attributes, or conversation parts based on your specific Intercom setup
  
  // For now, we'll return a placeholder structure
  return {
    complaints: getConversationAttribute(conversation, 'complaints'),
    start_time: getConversationAttribute(conversation, 'start_time'),
    characteristics: getConversationAttribute(conversation, 'characteristics'),
    pain_severity: getConversationAttribute(conversation, 'pain_severity'),
    other_symptoms: getConversationAttribute(conversation, 'other_symptoms'),
    medication_taken: getConversationAttribute(conversation, 'medication_taken'),
    improvement_with_medication: getConversationAttribute(conversation, 'improvement_with_medication'),
    additional_illnesses: getConversationAttribute(conversation, 'additional_illnesses'),
    additional_medications: getConversationAttribute(conversation, 'additional_medications')
  };
};

/**
 * Helper function to get attributes from conversation
 * @param {Object} conversation - The Intercom conversation object
 * @param {string} attributeName - The name of the attribute to extract
 * @returns {string} - The attribute value or empty string
 */
const getConversationAttribute = (conversation, attributeName) => {
  if (!conversation || !conversation.custom_attributes) {
    return '';
  }
  
  return conversation.custom_attributes[attributeName] || '';
};

/**
 * Get patient information from the Intercom user associated with the conversation
 * @param {Object} conversation - The Intercom conversation object
 * @returns {Object} - Patient information
 */
export const getPatientInfoFromConversation = (conversation) => {
  if (!conversation || !conversation.user) {
    return {
      name: 'Unknown Patient',
      age: 'N/A',
      gender: 'N/A'
    };
  }
  
  const user = conversation.user;
  
  return {
    name: user.name || 'Unknown Patient',
    age: user.custom_attributes?.age || 'N/A',
    gender: user.custom_attributes?.gender || 'N/A'
  };
};

/**
 * Get the current admin's Intercom ID
 * @returns {Promise<string>} - The admin's Intercom ID
 */
export const getCurrentAdminId = async () => {
  try {
    // In a real implementation, this would either:
    // 1. Be stored in your authentication context after login
    // 2. Be fetched from your backend which would have the mapping of your users to Intercom admins
    
    // For now, we'll return a placeholder
    return "123456"; // Replace with actual implementation
  } catch (error) {
    console.error('Error getting current admin ID:', error);
    throw error;
  }
}; 